import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { ReactComponent as ThreeDotsIcon } from '../../../assets/components/sidebar/ThreeDotsIcon.svg';
import { Lock } from 'lucide-react';

const MenuItem = ({
  item,
  isActive,
  isCollapsed,
  onToggleExpanded,
  onActionMenu,
  isLocked = false
}) => {
  const [tooltipPos, setTooltipPos] = useState(null);
  const itemRef = useRef(null);
  const iconRef = useRef(null);

  const handleClick = () => {
    onToggleExpanded(item);
  };

  const handleActionClick = (e) => {
    e.stopPropagation();
    if (item.hasActionMenu) {
      onActionMenu(e, item.id);
    }
  };

  const showTooltip = useCallback(() => {
    if (!isCollapsed) return;
    const rect = (iconRef.current || itemRef.current)?.getBoundingClientRect();
    if (!rect) return;
    setTooltipPos({ top: rect.top + rect.height / 2, left: rect.right + 12 });
  }, [isCollapsed]);

  const hideTooltip = useCallback(() => {
    setTooltipPos(null);
  }, []);

  return (
    <div
      className={`menu-item-container ${isActive ? 'active-container' : ''} ${isLocked ? 'menu-item-locked' : ''}`}
      ref={itemRef}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      <div
        className={`menu-item ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
        onClick={handleClick}
        style={isLocked ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
      >
        <span className="menu-icon" ref={iconRef}>{item.icon}</span>
        <span className="menu-text">
          {item.text}
        </span>
        {item.hasActionMenu && isActive && !isLocked && !isCollapsed && (
          <span
            className="action-menu-trigger"
            onClick={handleActionClick}
          >
            <ThreeDotsIcon />
          </span>
        )}
      </div>
      {tooltipPos && createPortal(
        <span
          className="menu-tooltip"
          style={{ top: tooltipPos.top, left: tooltipPos.left }}
        >
          {item.text}
        </span>,
        document.body
      )}
    </div>
  );
};

MenuItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
    hasActionMenu: PropTypes.bool
  }).isRequired,
  isActive: PropTypes.bool.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  onToggleExpanded: PropTypes.func.isRequired,
  onActionMenu: PropTypes.func,
  isLocked: PropTypes.bool
};

MenuItem.defaultProps = {
  isLocked: false
};

export default MenuItem;